// A2F-766: anonymous signup flow at `/`. Renders the v2 configurator
// body (extracted from the retired ProV2Page) inside the v2 signup
// header. Authenticated visitors never see this page —
// App.tsx::OnboardOrRedirect routes them to /configure or /install
// based on profile presence.
//
// Sections 1–5 live in ConfiguratorV2Body. Section 6 is rendered here
// as a SectionCard wrapping SectionSaveInstall (the email/password +
// trial-or-free signup form). The shared body and the per-page Section
// 6 fork at the page level — see ConfiguratorV2Body's renderInstallSection.

import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { Telemetry } from '../lib/telemetry'
import type { TierChoice } from '../components/SignupModal'
import { ProV2Header } from '../components/configurator-v2/ProV2Header'
import { SectionCard } from '../components/configurator-v2/SectionCard'
import { SectionSaveInstall } from '../components/configurator-v2/SectionSaveInstall'
import { ConfiguratorV2Body } from '../components/configurator-v2/ConfiguratorV2Body'
import { TermsPrivacyFooter } from '../components/TermsPrivacyFooter'
import { useAuth } from '../lib/AuthContext'

export function OnboardPage() {
  const { session, loading } = useAuth()
  const navigate = useNavigate()
  const [tier, setTier] = useState<TierChoice>('trial')

  useEffect(() => {
    Telemetry.setScreen('/')
    Telemetry.onboardingPageViewed({ has_session: !!session })
    Telemetry.configuratorPathLoaded({ path: 'signup' })
    // Mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) return null

  return (
    <div className="min-h-screen bg-brand-canvas text-brand-charcoal font-sans">
      <Helmet>
        <title>Chrona Pro — Build your ribbon</title>
        <meta
          name="description"
          content="Configure formatting, regulatory packs, and custom rules. Live preview as you go. Installs in Word."
        />
      </Helmet>
      <ProV2Header />
      <ConfiguratorV2Body
        mode="signup"
        renderInstallSection={({
          summary,
          outgoingProfile,
          isOpen,
          onToggle,
          edited,
          onFocus,
        }) => (
          <SectionCard
            id="install"
            number={6}
            title="Save & install"
            subtitle="Account creation is the last step. Trial unlocks every Pro feature for 14 days, no card."
            isOpen={isOpen}
            edited={edited}
            onToggle={onToggle}
            onFocus={onFocus}
            statusOverride={
              <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 bg-brand-orange/10 text-brand-orange">
                {summary.ribbonButtons} buttons · {summary.packs}{' '}
                {summary.packs === 1 ? 'pack' : 'packs'} · {summary.customRules}{' '}
                {summary.customRules === 1 ? 'rule' : 'rules'}
              </span>
            }
          >
            <SectionSaveInstall
              profile={outgoingProfile}
              tier={tier}
              onTierChange={setTier}
              summary={summary}
              onSent={(email, selectedTier) => {
                Telemetry.onboardingSuccessViewed({ tier: selectedTier })
                // A2F-913: signal /install (reached via the email
                // confirmation link) that this user finished the FRE
                // and already configured their ribbon — suppresses the
                // "ribbon ready to configure" nudge once on next visit.
                try {
                  localStorage.setItem('chrona-pro:fre-completed', '1')
                } catch {
                  // localStorage may be disabled (private mode); the
                  // banner will appear, which is the safe fallback.
                }
                navigate('/check-email', { state: { email } })
              }}
            />
          </SectionCard>
        )}
      />
      <TermsPrivacyFooter />
    </div>
  )
}
